import type { MutationMeta } from '../query-client/types';

interface ToastMessage {
  title: string;
  description: string;
}

/**
 * Pluralize a word based on count
 */
function pluralize(word: string, count: number): string {
  if (count === 1) return word;

  // Simple pluralization rules
  if (word.endsWith('y')) {
    return word.slice(0, -1) + 'ies';
  }
  if (word.endsWith('s') || word.endsWith('ch') || word.endsWith('sh')) {
    return word + 'es';
  }
  return word + 's';
}

/**
 * Generate success toast message from mutation metadata
 */
export function generateSuccessMessage(
  meta: MutationMeta,
  variables: unknown,
): ToastMessage | null {
  // Resolve dynamic values
  const count = typeof meta.count === 'function' ? meta.count(variables) : meta.count;
  const successMessage =
    typeof meta.successMessage === 'function'
      ? meta.successMessage(variables)
      : meta.successMessage;

  // Custom message takes precedence
  if (successMessage) {
    return {
      title: meta.successTitle || 'Success',
      description: successMessage,
    };
  }

  // Generate from action + resourceName
  if (meta.resourceName && meta.action) {
    const { resourceName, action } = meta;

    switch (action) {
      case 'create':
        return {
          title: 'Created',
          description: `${resourceName} created successfully`,
        };

      case 'update':
        return {
          title: 'Updated',
          description: `${resourceName} updated successfully`,
        };

      case 'delete':
        return {
          title: 'Deleted',
          description:
            count && count > 1
              ? `${count} ${pluralize(resourceName.toLowerCase(), count)} deleted`
              : `${resourceName} deleted successfully`,
        };

      case 'bulk':
        return {
          title: 'Completed',
          description: count
            ? `${count} ${pluralize(resourceName.toLowerCase(), count)} updated`
            : `Bulk operation completed successfully`,
        };
    }
  }

  return null;
}
